// src/views/NokiaSnake.jsx
import React, { useEffect, useRef, useState } from 'react';
import './NokiaSnake.css';

const GRID_SIZE = 20;
const INITIAL_SPEED = 150; // ms per tick

const directions = {
  ArrowUp:    { x: 0,  y: -1 },
  ArrowDown:  { x: 0,  y: 1 },
  ArrowLeft:  { x: -1, y: 0 },
  ArrowRight: { x: 1,  y: 0 },
};

function randomFood(exclude = []) {
  // Keep trying until we find a spot that's not on the snake
  while (true) {
    const x = Math.floor(Math.random() * GRID_SIZE);
    const y = Math.floor(Math.random() * GRID_SIZE);
    if (!exclude.some(s => s.x === x && s.y === y)) return { x, y };
  }
}

export default function NokiaSnake() {
  const containerRef = useRef(null);
  const [boardPx, setBoardPx] = useState(480); // dynamic board size
  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [food, setFood] = useState(() => randomFood([{ x: 10, y: 10 }]));
  const [direction, setDirection] = useState(directions.ArrowRight);
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const [gameOver, setGameOver] = useState(false);
  const [paused, setPaused] = useState(false);
  const [highScore, setHighScore] = useState(() => {
    try { return Number(localStorage.getItem('snake_highscore') || 0) || 0; } catch { return 0; }
  });
  const loopRef = useRef(null);

  // Resize board to ~75% of the available view
  useEffect(() => {
    const onResize = () => {
      const el = containerRef.current;
      const w = el?.clientWidth || window.innerWidth;
      const h = el?.clientHeight || window.innerHeight;
      const minSide = Math.min(w, h);
      const size = Math.floor(minSide * 0.75);
      const clamped = Math.max(300, Math.min(900, size));
      setBoardPx(clamped);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Key controls
  useEffect(() => {
    const onKey = (e) => {
      // Pause/Resume
      if (e.key === 'p' || e.key === 'P' || e.code === 'Space') {
        e.preventDefault();
        setPaused(p => !p);
        return;
      }
      // WASD and arrows
      const map = {
        KeyW: 'ArrowUp', ArrowUp: 'ArrowUp',
        KeyS: 'ArrowDown', ArrowDown: 'ArrowDown',
        KeyA: 'ArrowLeft', ArrowLeft: 'ArrowLeft',
        KeyD: 'ArrowRight', ArrowRight: 'ArrowRight',
      };
      const key = map[e.code] || e.key;
      const next = directions[key];
      if (!next) return;
      // prevent reversing directly
      if (next.x !== -direction.x || next.y !== -direction.y) {
        setDirection(next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [direction]);

  // Main loop
  useEffect(() => {
    if (gameOver || paused) return;
    loopRef.current = setInterval(() => {
      setSnake(prev => {
        const head = { x: prev[0].x + direction.x, y: prev[0].y + direction.y };

        // Wall collision
        if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
          setGameOver(true);
          return prev;
        }

        // Self collision
        if (prev.some(p => p.x === head.x && p.y === head.y)) {
          setGameOver(true);
          return prev;
        }

        const nextSnake = [head, ...prev];
        // Eat food
        if (head.x === food.x && head.y === food.y) {
          setFood(randomFood(nextSnake));
          setSpeed(s => Math.max(50, s - 5));
          return nextSnake; // grow
        }
        // move
        nextSnake.pop();
        return nextSnake;
      });
    }, speed);
    return () => clearInterval(loopRef.current);
  }, [direction, food, gameOver, paused, speed]);

  // Track high score
  useEffect(() => {
    const score = snake.length - 1;
    if (score > highScore) {
      setHighScore(score);
      try { localStorage.setItem('snake_highscore', String(score)); } catch {}
    }
  }, [snake, highScore]);

  const reset = () => {
    setSnake([{ x: 10, y: 10 }]);
    setFood(randomFood([{ x: 10, y: 10 }]));
    setDirection(directions.ArrowRight);
    setSpeed(INITIAL_SPEED);
    setGameOver(false);
    setPaused(false);
  };

  return (
    <div className="snake-container" ref={containerRef}>
      <div className="snake-header">
        <h2 className="snake-title">Nokia Snake</h2>
        <div className="snake-score">Score: {snake.length - 1} · High: {highScore}</div>
        <div className="snake-hint">Press P or Space to {paused ? 'Resume' : 'Pause'}</div>
      </div>
      <div className="snake-board" style={{ width: boardPx, height: boardPx, gridTemplateColumns: `repeat(${GRID_SIZE}, ${Math.floor(boardPx/GRID_SIZE)}px)`, gridTemplateRows: `repeat(${GRID_SIZE}, ${Math.floor(boardPx/GRID_SIZE)}px)` }}>
        {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, idx) => {
          const x = idx % GRID_SIZE;
          const y = Math.floor(idx / GRID_SIZE);
          const isSnake = snake.some(s => s.x === x && s.y === y);
          const isFood  = food.x === x && food.y === y;
          return (
            <div key={idx} className={`cell${isSnake ? ' snake' : ''}${isFood ? ' food' : ''}`} />
          );
        })}
      </div>
      {(gameOver || paused) && (
        <div className="snake-overlay">
          <div className="overlay-card">
            {gameOver ? (
              <>
                <h3>Game Over</h3>
                <p>Your final score is {snake.length - 1}</p>
                <button className="btn primary" onClick={reset}>Restart</button>
              </>
            ) : (
              <>
                <h3>Paused</h3>
                <p>Press P or Space to resume</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
